// Product image type matching OpenAPI spec
export interface ProductImage {
  id?: string;
  cid?: string;
  filename?: string;
  url: string;
  sortOrder?: number;
  isThumbnail?: boolean;
}

// Product attribute type - can be a string or an object with key-value pairs
export type ProductAttribute = string | Record<string, string>;

// Helper function to format an attribute for display
export function formatAttribute(attr: ProductAttribute): string {
  if (typeof attr === 'string') {
    return attr;
  }
  // It's an object - format as "key: value"
  const entries = Object.entries(attr);
  if (entries.length === 0) {
    return '';
  }
  // Return first key-value pair formatted nicely
  const [key, value] = entries[0];
  return `${key}: ${value}`;
}

// Helper function to get a unique key for an attribute (for React key prop)
export function getAttributeKey(attr: ProductAttribute, index: number): string {
  if (typeof attr === 'string') {
    return attr;
  }
  // For objects, create a key from the first entry or use index as fallback
  const entries = Object.entries(attr);
  if (entries.length === 0) {
    return `attr-${index}`;
  }
  const [key, value] = entries[0];
  return `${key}-${value}`;
}

// Product type matching OpenAPI spec response
export interface Product {
  id: string;
  productCode: string;
  name: string;
  title: string;
  description?: string | null;
  categoryId: string;
  price: number;
  cost?: number | null;
  sku?: string | null;
  status: 'draft' | 'published' | 'archived';
  thumbnailUrl?: string | null;
  categoryName: string,
  images: ProductImage[];
  attributes: ProductAttribute[];
  variants?: unknown[];
  tags?: unknown[];
  sections?: unknown[];
  createdAt: string;
  updatedAt: string;
}

// Create product request type matching OpenAPI spec
export interface CreateProduct {
  name: string;
  title: string;
  description?: string;
  categoryId: string;
  sku?: string | null;
  status?: 'draft' | 'published' | 'archived';
  cost?: number | null;
  attributes?: string[];
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategory {
  name: string;
  slug: string;
  description?: string;
  thumbnailUrl?: string;
  isActive?: boolean;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export type OrderStatus = 'NEW' | 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';

export interface Address {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id?: string | null;
  sku?: string | null;
  name: string;
  quantity: number;
  unit_price: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  metadata?: Record<string, any> | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  customer: string;
  email: string;
  items: number;
  total: number;
  status: OrderStatus;
  date: string;
  externalOrderId?: string | null;
  currency: string;
}

export interface ApiOrder {
  id: string;
  client_id: string;
  external_order_id?: string | null;
  external_platform?: string | null;
  customer_sub?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  currency: string;
  subtotal_amount: number;
  tax_amount: number;
  shipping_amount: number;
  discount_amount: number;
  total_amount: number;
  status: OrderStatus;
  idempotency_key?: string | null;
  version: number;
  shipping_address?: Address | null;
  billing_address?: Address | null;
  metadata?: Record<string, any> | null;
  cash_on_delivery?: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface Payment {
  id: string;
  client_id: string;
  order_id: string;
  provider: 'cashfree' | 'razorpay' | 'cash_on_delivery';
  provider_order_id: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  payment_method?: string | null;
  provider_response?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOrderRequest {
  idempotency_key: string;
  currency: string;
  subtotal_amount: number;
  tax_amount: number;
  shipping_amount: number;
  discount_amount: number;
  total_amount: number;
  status: OrderStatus;
  customer_sub?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  shipping_address?: Address | null;
  billing_address?: Address | null;
  metadata?: Record<string, any> | null;
  items: OrderItem[];
}

export interface UpdateOrderRequest {
  idempotency_key: string;
  status?: OrderStatus | null;
  metadata?: Record<string, any> | null;
}

export interface CancelOrderRequest {
  idempotency_key: string;
  reason: string;
}

export interface AddCommentRequest {
  visibility: 'public' | 'private' | 'internal';
  body: string;
  attachments?: CommentAttachment[];
}

export interface CommentAttachment {
  id: string;
  client_id: string;
  comment_id: string;
  url: string;
  content_type?: string | null;
  file_name?: string | null;
  file_size_bytes?: number | null;
  metadata?: string | null;
  created_at: string;
  deleted_at?: string | null;
}

export interface Comment {
  id: string;
  client_id: string;
  order_id: string;
  author_sub?: string | null;
  author_email?: string | null;
  author_name?: string | null;
  author_role: 'user' | 'admin' | 'support' | 'system';
  visibility: 'public' | 'private' | 'internal';
  body: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface DashboardStats {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
}

export interface SalesData {
  name: string;
  sales: number;
}

export interface CategoryData {
  name: string;
  value: number;
}



export interface Settings {
  storeName: string;
  storeEmail: string;
  storePhone: string;
  currency: string;
  timezone: string;
  taxRate: number;
  shippingEnabled: boolean;
  notifications: NotificationSettings;
}

export interface NotificationSettings {
  orderNotifications: boolean;
  lowStockAlerts: boolean;
  customerMessages: boolean;
  marketingUpdates: boolean;
}

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  fullName: string;
  avatar?: string;
  role: string;
}

export interface Configuration {
  key: string;
  value: string;
  description: string | null;
  config_display_name: string | null;
  config_group: string | null;
}

export interface ConfigListResponse {
  configs: Configuration[];
}

// Payment Gateway types
export type PaymentGatewayProvider = 'none' | 'stripe' | 'paypal' | 'cashfree' | 'razorpay';

export interface PaymentGatewayConfig {
  provider: PaymentGatewayProvider;
  enabled: boolean;
  api_key?: string | null;
  api_key_id?: string | null;
  api_secret?: string | null;
  webhook_secret?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface UpsertPaymentGatewayRequest {
  provider: PaymentGatewayProvider;
  api_key: string;
  api_key_id?: string;
  api_secret: string;
  webhook_secret?: string;
}
